/**
 * Verbose tracing of the boot → launch → first-frame path.
 *
 * That stretch is otherwise silent, so a failure anywhere in it looks identical from the outside: a
 * launch spinner that never clears. One prefix throughout, so a phone console can be filtered to just
 * this. Off unless asked for — see `setDebugLogging`.
 *
 * Everything is flattened to a STRING rather than logged as an object. These logs are read on a phone
 * and pasted into a chat, where a console object collapses to the word "Object" and takes its contents
 * with it.
 */

let _on = false;

try {
    _on = new URLSearchParams(location.search).has('juteDebug');
} catch {
    // No URL to read (a worker, a test): stay off and wait for setDebugLogging.
}

/** Turn tracing on. Called once the version is known, since a `dev/` release always wants it. */
export const setDebugLogging = (on: boolean): void => {
    _on = _on || on;
};

export const isDebugLogging = (): boolean => _on;

/**
 * One value as a single-line string. `undefined` is spelled out rather than dropped, because a flag
 * being absent is usually the thing worth seeing.
 */
export const debugFormat = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    try {
        const seen = new WeakSet<object>();
        return (
            JSON.stringify(value, (_key, val) => {
                if (typeof val === 'object' && val !== null) {
                    if (seen.has(val as object)) return '[circular]';
                    seen.add(val as object);
                }
                return typeof val === 'undefined' ? '[undefined]' : val;
            }) ?? String(value)
        );
    } catch {
        return String(value);
    }
};

export const debugLog = (...args: unknown[]): void => {
    if (!_on) return;
    console.log('[jute-debug]', args.map(debugFormat).join(' '));
};
